// src/components/table/AssetGenerator.tsx
import React, { useEffect, useRef, useMemo, useState } from "react";
import { Button } from "../ui/button";
import { useSQLiteDatabase } from "@/core/sqlite/useSQLiteDatabase";
import { useToast, cn } from "@/lib";

import {
  Database,
  FileSymlink,
  ClipboardCopy,
  Check,
  X,
  Search,
  RefreshCw,
  Filter,
  Tag,
  Calendar,
  Layers,
  Code,
  HelpCircle,
  ArrowUpDown,
  Settings,
} from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { ToggleGroup, ToggleGroupItem } from "../ui/toggle-group";
import { Card } from "../ui/card";
import { Input } from "../ui/input";

/* ---------------------------- shared typings ----------------------------- */

interface DatasetRow {
  id: string;
  name: string;
  description?: string;
  partitioned?: boolean;
  io_manager_key?: string;
  data_url?: string;
  r2_url?: string;
  start_year?: number;
  start_month?: number;
  end_year?: number;
  end_month?: number;
  tags: string[];
}

interface AssetGeneratorProps {
  conn: any;
}

/* ------------------------- helper: python template ----------------------- */

function buildPython(selected: DatasetRowWithWindow[]): string {
  const header = `from dagster import asset, MetadataValue\n\n`;
  const body = selected
    .map((ds) => {
      const safeGroup = ds.tags?.[0] || "misc";
      const isPartitioned =
        ds.io_manager_key?.includes("partitioned") || ds.partitioned;
      return `@asset(
    name="${ds.name}",
    io_manager_key="${ds.io_manager_key}",
    group_name="${safeGroup}",
    tags={"domain": "${safeGroup}", "type": "${
        isPartitioned ? "partitioned" : "single_file"
      }"},
    metadata={"data_url": MetadataValue.url("${ds.data_url || ds.r2_url}")},
)
def ${ds.name}():
${
  isPartitioned
    ? `    return {
        "start_year": ${ds.start_year},
        "start_month": ${ds.start_month},
        "end_year": ${ds.end_year},
        "end_month": ${ds.end_month},
    }`
    : "    return None"
}`;
    })
    .join("\n\n");
  return header + body;
}

/* ---------------------- helper: dbt-YAML template ------------------------ */

function buildDBTYaml(selected: DatasetRowWithWindow[]): string {
  const tables = selected
    .map(
      (ds) => `  - name: ${ds.name}
    meta:
      dagster:
        asset_key:
        - ${ds.name}`
    )
    .join("\n");
  return `version: 2
sources:
- name: main
  tables:
${tables}
`;
}

/* ----------------------- helper: datasets.py ---------------------------- */
function buildDatasetsPy(selected: DatasetRowWithWindow[]): string {
  const singles = selected
    .filter((d) => !(d.partitioned || d.io_manager_key?.includes("partitioned")))
    .map((d) => `"${d.name}"`)
    .sort()
    .join(",\n    ");

  const parts = selected
    .filter((d) => d.partitioned || d.io_manager_key?.includes("partitioned"))
    .map((d) => `"${d.name}"`)
    .sort()
    .join(",\n    ");

  return `# Single File Asset Names
SINGLE_FILE_ASSETS_NAMES = [
    ${singles}
]

# Partitioned Asset Names
PARTITIONED_ASSETS_NAMES = [
    ${parts}
]
`;
}

/* ------------------- helper: duckdb_warehouse.py ------------------------ */
const WAREHOUSE_TEMPLATE = `import os
from pathlib import Path
from dagster import asset, AssetExecutionContext

from pipeline.constants import (
    SINGLE_FILE_ASSETS_PATHS,
    PARTITIONED_ASSETS_PATHS,
    WAREHOUSE_PATH,
)
from pipeline.utils.duckdb_wrapper import DuckDBWrapper

@asset(
    deps=[
__DEPS_PLACEHOLDER__
    ],
    compute_kind="DuckDB",
    group_name="Warehouse",
)
def duckdb_warehouse(context: AssetExecutionContext):
    """
    Creates a persistent DuckDB file at WAREHOUSE_PATH and registers each
    asset as a DuckDB view. Partitioned assets use a different method.
    """

    # 1) Ensure the output directory exists
    warehouse_dir = Path(WAREHOUSE_PATH).parent
    warehouse_dir.mkdir(parents=True, exist_ok=True)

    # 2) Remove any existing DuckDB file
    if os.path.exists(WAREHOUSE_PATH):
        os.remove(WAREHOUSE_PATH)
        context.log.info(f"Existing DuckDB file at {WAREHOUSE_PATH} deleted.")

    # 3) Create a new DuckDB connection (and file)
    duckdb_wrapper = DuckDBWrapper(WAREHOUSE_PATH)
    context.log.info(f"New DuckDB file created at {WAREHOUSE_PATH}")

    # 4) Register non-partitioned assets
    non_partitioned = SINGLE_FILE_ASSETS_PATHS
    if non_partitioned:
        table_names = list(non_partitioned.keys())
        repo_root = os.path.dirname(WAREHOUSE_PATH)
        first_path = next(iter(non_partitioned.values()))
        base_path = os.path.relpath(os.path.dirname(first_path), repo_root)

        duckdb_wrapper.bulk_register_data(
            repo_root=repo_root,
            base_path=base_path,
            table_names=table_names,
            wildcard="*.parquet",
            as_table=False,
            show_tables=False,
        )

    # 5) Register partitioned assets
    partitioned = PARTITIONED_ASSETS_PATHS
    if partitioned:
        table_names = list(partitioned.keys())
        repo_root = os.path.dirname(WAREHOUSE_PATH)
        first_path = next(iter(partitioned.values()))
        base_path = os.path.relpath(os.path.dirname(first_path), repo_root)

        duckdb_wrapper.bulk_register_partitioned_data(
            repo_root=repo_root,
            base_path=base_path,
            table_names=table_names,
            wildcard="year=*/month=*/*.parquet",
            as_table=False,
            show_tables=False,
        )

    # 6) Clean up
    duckdb_wrapper.con.close()
    context.log.info("Connection to DuckDB closed.")

    return None
`;

function buildDuckdbWarehousePy(selected: DatasetRowWithWindow[]): string {
  const singles = selected
    .filter((d) => !(d.partitioned || d.io_manager_key?.includes("partitioned")))
    .map((d) => `"${d.name}"`)
    .sort();

  const parts = selected
    .filter((d) => d.partitioned || d.io_manager_key?.includes("partitioned"))
    .map((d) => `"${d.name}"`)
    .sort();

  const deps = [...singles, ...parts].join(",\n        ");

  return WAREHOUSE_TEMPLATE.replace("__DEPS_PLACEHOLDER__", `        ${deps}`);
}

/* ------------------- helper: definitions.py (static) -------------------- */
const DEFINITIONS_TEMPLATE = `import os
from dagster import Definitions, load_assets_from_modules
from pipeline.constants import LAKE_PATH

# 1) Ingestion assets module
import pipeline.assets.ingestion.custom_assets as ingestion_module

# 2) Warehouse asset module
import pipeline.assets.warehouse.duckdb_warehouse as warehouse_module

# 3) DBT assets and resource
from pipeline.assets.dbt_assets import dbt_project_assets, dbt_project
from dagster_dbt import DbtCliResource

dbt = DbtCliResource(project_dir=dbt_project.project_dir)

# 4) IO Managers
from pipeline.resources.io_managers.fastopendata_single_io_manager import FastOpenDataSingleFileParquetIoManager
from pipeline.resources.io_managers.fastopendata_partitioned_parquet_io_manager import FastOpenDataPartitionedParquetIOManager

singlefile_io_manager = FastOpenDataSingleFileParquetIoManager(base_dir=LAKE_PATH)
partitioned_io_manager = FastOpenDataPartitionedParquetIOManager(base_dir=LAKE_PATH)

# 5) Load assets from each module
ingestion_assets = load_assets_from_modules([ingestion_module])
warehouse_assets = load_assets_from_modules([warehouse_module])

# 6) Combine all asset definitions
all_assets = [
    *ingestion_assets,
    *warehouse_assets,
    dbt_project_assets,
]

# 7) Wire up resources
resources = {
    "dbt": dbt,
    "fastopendata_singlefile_io_manager": singlefile_io_manager,
    "fastopendata_partitioned_parquet_io_manager": partitioned_io_manager,
}

# 8) Final Definitions
defs = Definitions(
    assets=all_assets,
    resources=resources,
)
`;

/* ---------------------- internal type with window ------------------------ */

type DatasetRowWithWindow = DatasetRow & {
  start_year: number;
  start_month: number;
  end_year: number;
  end_month: number;
};

/* ======================================================================== */

export default function AssetGenerator({}: AssetGeneratorProps) {
  const { db, error, loading } = useSQLiteDatabase(
    "https://fastopendata.org/mytest.sqlite"
  );
  const { toast } = useToast();

  const [rows, setRows] = useState<DatasetRow[]>([]);
  const [filteredRows, setFilteredRows] = useState<DatasetRow[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<{
    hasResults: boolean;
    count: number;
  }>({ hasResults: true, count: 0 });
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  /* new state ------------------------------------------------------------ */
  const [pythonCode, setPythonCode] = useState("");
  const [dbtCode, setDbtCode] = useState("");
  const [datasetsCode, setDatasetsCode] = useState("");
  const [warehouseCode, setWarehouseCode] = useState("");
  const [codeMode, setCodeMode] =
  useState<"python" | "dbt" | "datasets" | "warehouse" | "definitions">(
    "python",
  );
  const [copied, setCopied] = useState(false);
  /* --------------------------------------------------------------------- */

  const [filterType, setFilterType] = useState<string>("all");
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [filteredTags, setFilteredTags] = useState<string[]>([]);
  const [tagSearchTerm, setTagSearchTerm] = useState("");
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [tagMenuOpen, setTagMenuOpen] = useState(false);
  const tagMenuRef = useRef<HTMLDivElement>(null);

  /* ------------------- load catalog once -------------------------------- */

  useEffect(() => {
    if (!db) return;
    try {
      const res = db.exec(
        `SELECT *,
                COALESCE(start_year, 2020) AS start_year,
                COALESCE(start_month, 1)   AS start_month,
                COALESCE(end_year, 2024)   AS end_year,
                COALESCE(end_month, 12)    AS end_month
         FROM datasets
         ORDER BY name`
      );
      if (!res.length) return;

      const cols = res[0].columns;
      const allTags = new Set<string>();

      const objs: DatasetRow[] = res[0].values.map((row) => {
        const o: any = {};
        cols.forEach((c, i) => (o[c] = row[i]));
        try {
          o.tags =
            typeof o.tags === "string" ? JSON.parse(o.tags) ?? [] : o.tags ?? [];
        } catch {
          o.tags = [];
        }
        o.tags
          .filter((t: any) => typeof t === "string")
          .forEach((t: string) => allTags.add(t));
        return o as DatasetRow;
      });

      setRows(objs);
      setFilteredRows(objs);
      const sortedTags = Array.from(allTags).sort();
      setAvailableTags(sortedTags);
      setFilteredTags(sortedTags);
    } catch (e: any) {
      toast({
        title: "SQLite error",
        description: e.message,
        variant: "destructive",
      });
    }
  }, [db, toast]);

  /* ----------------------- filtering & sorting -------------------------- */

  useEffect(() => {
    let f = [...rows];
    if (searchTerm.trim()) {
      const terms = searchTerm.toLowerCase().split(/\s+/);
      f = f.filter((r) =>
        terms.every(
          (t) =>
            r.name.toLowerCase().includes(t) ||
            (r.description ?? "").toLowerCase().includes(t) ||
            r.tags.some(
              (tag) => typeof tag === "string" && tag.toLowerCase().includes(t)
            )
        )
      );
    }
    if (selectedTags.length)
      f = f.filter((r) =>
        selectedTags.every((tag) => r.tags.includes(tag))
      );
    if (filterType === "partitioned")
      f = f.filter(
        (r) => r.partitioned || r.io_manager_key?.includes("partitioned")
      );
    if (filterType === "single")
      f = f.filter(
        (r) => !(r.partitioned || r.io_manager_key?.includes("partitioned"))
      );

    setSearchResults({ hasResults: !!f.length, count: f.length });
    setFilteredRows(f);
  }, [rows, searchTerm, selectedTags, filterType]);

  /* ----------------------- utility -------------------------------------- */

  const toggle = (id: string) =>
    setSelectedIds((s) =>
      s.includes(id) ? s.filter((x) => x !== id) : [...s, id]
    );

  /* -------------------- selection helpers ------------------------------- */

  const selectFiltered = () =>
    setSelectedIds((prev) => [
      ...new Set([...prev, ...filteredRows.map((r) => r.id)]),
    ]);

  const clearSelection = () => setSelectedIds([]);

  /* --------------------------- memo rows -------------------------------- */

  const selectedRowsWithWindow: DatasetRowWithWindow[] = useMemo(
    () =>
      rows
        .filter((r) => selectedIds.includes(r.id))
        .map((r) => ({
          ...r,
          start_year: r.start_year ?? 2020,
          start_month: r.start_month ?? 1,
          end_year: r.end_year ?? 2024,
          end_month: r.end_month ?? 12,
        })),
    [rows, selectedIds]
  );

  /* ---------------------- handle window change -------------------------- */

  const updateWindow = (
    id: string,
    field: "start_year" | "start_month" | "end_year" | "end_month",
    val: number
  ) =>
    setRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, [field]: Number(val) } : r))
    );

  /* ----------------------- generate code ------------------------------- */

  const handleGenerate = () => {
    const selected = selectedRowsWithWindow;
    setPythonCode(buildPython(selected));
    setDbtCode(buildDBTYaml(selected));
    setDatasetsCode(buildDatasetsPy(selected));
    setWarehouseCode(buildDuckdbWarehousePy(selected));
    setCodeMode("python");
    setDialogOpen(true);
    setCopied(false);
  };
  
  const handleCopy = async () => {
    await navigator.clipboard.writeText(
      codeMode === "python"
        ? pythonCode
        : codeMode === "dbt"
        ? dbtCode
        : codeMode === "datasets"
        ? datasetsCode
        : codeMode === "warehouse"
        ? warehouseCode
        : DEFINITIONS_TEMPLATE,
    );
    setCopied(true);
    toast({
      title: "Copied!",
      description:
        codeMode === "python"
          ? "custom_assets.py copied."
          : codeMode === "dbt"
          ? "sources.yml copied."
          : codeMode === "datasets"
          ? "datasets.py copied."
          : codeMode === "warehouse"
          ? "duckdb_warehouse.py copied."
          : "definitions.py copied.",
    });
  };

  const download = (name: string, content: string) => {
    const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    a.click();
    URL.revokeObjectURL(url);
  };
  /* --------------------------- render ----------------------------------- */

  if (loading)
    return (
      <div className="flex items-center justify-center h-full">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin h-12 w-12 border-4 border-t-brand rounded-full"></div>
          <p className="text-muted-foreground animate-pulse">Loading asset catalog...</p>
        </div>
      </div>
    );
  if (error)
    return (
      <div className="p-8 flex justify-center">
        <Card className="p-6 border-red-300 bg-red-50 dark:bg-red-950/20 max-w-md">
          <div className="flex items-center gap-3 text-red-600 mb-4">
            <Database className="w-8 h-8" />
            <h3 className="text-xl font-semibold">Database Error</h3>
          </div>
          <p className="mb-4">{error.message}</p>
          <Button 
            variant="outline" 
            className="w-full"
            onClick={() => window.location.reload()}
          >
            <RefreshCw className="w-4 h-4 mr-2" /> Try Again
          </Button>
        </Card>
      </div>
    );

  // Calculate some metrics for the UI
  const totalDatasets = rows.length;
  const selectedCount = selectedIds.length;
  const partitionedCount = selectedRowsWithWindow.filter(r => 
    r.io_manager_key?.includes("partitioned") || r.partitioned
  ).length;
  const singleFileCount = selectedCount - partitionedCount;

  return (
    <>
      <div className="flex flex-col gap-6 h-full p-4 overflow-hidden">
        {/* Header with metrics */}
        <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold flex items-center gap-2 mb-1">
              <Code className="w-6 h-6 text-brand" />
              Dagster Asset Generator
            </h2>
            <p className="text-sm text-muted-foreground">
              Select datasets and configure code generation for your Dagster pipeline
            </p>
          </div>
          
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setHelpOpen(true)}
              className="text-muted-foreground"
            >
              <HelpCircle className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">Help</span>
            </Button>
            
            <Button
              variant="default"
              size="sm"
              disabled={!selectedIds.length}
              onClick={handleGenerate}
              className="bg-brand hover:bg-brand/90"
            >
              <FileSymlink className="w-4 h-4 mr-1" />
              Generate Assets
            </Button>
          </div>
        </div>
        
        {/* Search and filters */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="md:col-span-2 relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search datasets by name, description or tags..."
              className={cn(
                "pl-9 w-full",
                searchTerm && !searchResults.hasResults && "border-red-300 focus-visible:ring-red-300"
              )}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button
                className="absolute right-2.5 top-2.5 text-muted-foreground hover:text-foreground"
                onClick={() => setSearchTerm("")}
                title="Clear search"
              >
                <X className="h-4 w-4" />
              </button>
            )}
            {searchTerm && searchResults.count > 0 && (
              <div className="absolute right-10 top-2.5 text-xs text-muted-foreground">
                {searchResults.count} result{searchResults.count !== 1 ? 's' : ''}
              </div>
            )}
          </div>
          
          <div>
            <div className="inline-flex items-center rounded-md border border-input bg-transparent px-4 py-2 text-sm w-full">
              <Filter className="mr-2 h-4 w-4 text-brand" />
              <select 
                className="bg-transparent outline-none w-full"
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
              >
                <option value="all">All Types</option>
                <option value="partitioned">Partitioned</option>
                <option value="single">Single File</option>
              </select>
            </div>
          </div>
          
          <div className="relative">
            <button
              className={cn(
                "inline-flex items-center justify-between w-full rounded-md border border-input bg-transparent px-4 py-2 text-sm",
                "hover:bg-muted/20 focus:outline-none focus:ring-2 focus:ring-brand focus:ring-opacity-50",
                selectedTags.length > 0 && "border-brand/60 bg-brand/5"
              )}
              onClick={() => setTagMenuOpen(!tagMenuOpen)}
            >
              <div className="flex items-center">
                <Tag className="mr-2 h-4 w-4 text-brand" />
                {selectedTags.length === 0 ? (
                  <span className="text-muted-foreground">Filter by tags</span>
                ) : (
                  <span>{selectedTags.length} tag{selectedTags.length !== 1 ? 's' : ''} selected</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {selectedTags.length > 0 && (
                  <button 
                    className="text-muted-foreground hover:text-foreground"
                    onClick={(e) => {
                      e.stopPropagation();
                      clearTags();
                    }}
                    title="Clear tags"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
                <ArrowUpDown className="h-3.5 w-3.5 text-muted-foreground" />
              </div>
            </button>
            
            {tagMenuOpen && (
              <div 
                ref={tagMenuRef}
                className="absolute z-10 mt-1 w-full max-h-72 overflow-auto rounded-md border bg-background shadow-lg"
              >
                <div className="p-2 border-b sticky top-0 bg-background z-10">
                  <div className="flex justify-between items-center mb-1.5">
                    <div className="text-xs font-medium text-muted-foreground">
                      {availableTags.length} available tags
                    </div>
                    {selectedTags.length > 0 && (
                      <button
                        className="text-xs text-brand hover:text-brand/80"
                        onClick={() => clearTags()}
                      >
                        Clear all
                      </button>
                    )}
                  </div>
                  <div className="relative">
                    <Search className="absolute left-2 top-1.5 h-3.5 w-3.5 text-muted-foreground" />
                    <Input 
                      className="h-7 pl-7 text-xs"
                      placeholder="Search tags..."
                      value={tagSearchTerm}
                      onChange={(e) => setTagSearchTerm(e.target.value)}
                    />
                    {tagSearchTerm && (
                      <button
                        className="absolute right-2 top-1.5 text-muted-foreground hover:text-foreground"
                        onClick={() => setTagSearchTerm("")}
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                  {filteredTags.length > 0 && filteredTags.length < availableTags.length && (
                    <div className="mt-2 flex justify-between items-center">
                      <span className="text-xs text-muted-foreground">
                        {filteredTags.length} matching tags
                      </span>
                      <button
                        className="text-xs px-2 py-0.5 bg-brand/10 text-brand rounded hover:bg-brand/20"
                        onClick={() => selectAllFilteredTags()}
                      >
                        Select all filtered
                      </button>
                    </div>
                  )}
                </div>
                <div className="py-1">
                  {filteredTags.length === 0 ? (
                    <div className="px-3 py-4 text-sm text-muted-foreground text-center">
                      {availableTags.length === 0 ? (
                        "No tags available"
                      ) : (
                        <>
                          <Search className="inline-block w-4 h-4 mb-1 opacity-40" />
                          <p>No tags match "{tagSearchTerm}"</p>
                        </>
                      )}
                    </div>
                  ) : (
                    filteredTags.map(tag => (
                      <div 
                        key={tag}
                        className={cn(
                          "px-3 py-1.5 flex items-center cursor-pointer text-sm",
                          "hover:bg-muted/50",
                          selectedTags.includes(tag) && "bg-brand/10"
                        )}
                        onClick={() => toggleTag(tag)}
                      >
                        <input 
                          type="checkbox" 
                          className="mr-2 rounded"
                          checked={selectedTags.includes(tag)}
                          onChange={() => {}} // Handled by the parent div onClick
                          onClick={(e) => e.stopPropagation()}
                        />
                        <span>{tag}</span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
          
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              className="flex-1 text-xs"
              onClick={selectFiltered}
            >
              <Tag className="w-3.5 h-3.5 mr-1" />
              Select Filtered
            </Button>
            <Button 
              variant="outline"
              className="flex-1 text-xs"
              onClick={clearSelection}
            >
              <X className="w-3.5 h-3.5 mr-1" />
              Clear
            </Button>
          </div>
        </div>
        
        {/* Selection summary */}
        <div className="grid grid-cols-1 sm:grid-cols-5 gap-3 mb-1">
          <Card className="p-3 flex items-center gap-3 border-brand/20">
            <div className="bg-brand/10 rounded-md p-2">
              <Database className="w-5 h-5 text-brand" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Total Datasets</p>
              <p className="text-lg font-semibold">{totalDatasets}</p>
            </div>
          </Card>
          
          <Card className="p-3 flex items-center gap-3 border-indigo-500/20">
            <div className="bg-indigo-500/10 rounded-md p-2">
              <Tag className="w-5 h-5 text-indigo-500" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Selected</p>
              <p className="text-lg font-semibold">{selectedCount}</p>
            </div>
          </Card>
          
          <Card className="p-3 flex items-center gap-3 border-amber-500/20">
            <div className="bg-amber-500/10 rounded-md p-2">
              <Calendar className="w-5 h-5 text-amber-500" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Partitioned</p>
              <p className="text-lg font-semibold">{partitionedCount}</p>
            </div>
          </Card>
          
          <Card className="p-3 flex items-center gap-3 border-emerald-500/20">
            <div className="bg-emerald-500/10 rounded-md p-2">
              <Layers className="w-5 h-5 text-emerald-500" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Single File</p>
              <p className="text-lg font-semibold">{singleFileCount}</p>
            </div>
          </Card>
          
          <Card 
            className={cn(
              "p-3 flex items-center gap-3",
              selectedTags.length > 0 
                ? "border-purple-500/20" 
                : "border-slate-200 dark:border-slate-800"
            )}
          >
            <div className={cn(
              "rounded-md p-2",
              selectedTags.length > 0 
                ? "bg-purple-500/10" 
                : "bg-slate-100 dark:bg-slate-800"
            )}>
              <Tag className={cn(
                "w-5 h-5",
                selectedTags.length > 0 
                  ? "text-purple-500" 
                  : "text-slate-400"
              )} />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Active Tags</p>
              <p className="text-lg font-semibold">{selectedTags.length}</p>
            </div>
          </Card>
        </div>

        {/* Dataset table with improved styling */}
        <div className="border rounded-lg flex-1 overflow-y-auto shadow-sm bg-white dark:bg-slate-900 max-h-[calc(100vh-400px)]">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-muted/50 backdrop-blur-sm z-10">
              <tr className="border-b">
                <th className="p-3 w-12">
                  <div className="flex justify-center">
                    <input
                      type="checkbox"
                      className="rounded"
                      checked={filteredRows.length > 0 && filteredRows.every(r => selectedIds.includes(r.id))}
                      onChange={() => {
                        if (filteredRows.every(r => selectedIds.includes(r.id))) {
                          setSelectedIds(prev => prev.filter(id => !filteredRows.some(r => r.id === id)));
                        } else {
                          selectFiltered();
                        }
                      }}
                    />
                  </div>
                </th>
                <th className="text-left p-3 font-medium">
                  <div className="flex items-center gap-1">
                    Name
                    <ArrowUpDown className="w-3 h-3 text-muted-foreground" />
                  </div>
                </th>
                <th className="text-left p-3 font-medium">Description</th>
                <th className="text-left p-3 font-medium w-28">Type</th>
                <th className="text-center p-3 font-medium w-44">
                  <div className="flex items-center justify-center gap-1">
                    <Calendar className="w-3.5 h-3.5" />
                    <span>Date Window</span>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center py-8 text-muted-foreground">
                    <div className="flex flex-col items-center">
                      <Search className="w-8 h-8 mb-2 opacity-30" />
                      {searchTerm ? (
                        <>
                          <p>No matching datasets found for "{searchTerm}"</p>
                          <p className="text-xs mt-1">Try using different keywords or checking for typos</p>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="mt-3"
                            onClick={() => setSearchTerm("")}
                          >
                            <X className="w-3.5 h-3.5 mr-1" /> Clear Search
                          </Button>
                        </>
                      ) : filterType !== "all" ? (
                        <>
                          <p>No {filterType} datasets found</p>
                          <p className="text-xs mt-1">Try changing the filter selection</p>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="mt-3"
                            onClick={() => setFilterType("all")}
                          >
                            <Filter className="w-3.5 h-3.5 mr-1" /> Show All Types
                          </Button>
                        </>
                      ) : (
                        <>
                          <p>No datasets available</p>
                          <p className="text-xs mt-1">Check your database connection</p>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                filteredRows.map((r) => {
                  const isSel = selectedIds.includes(r.id);
                  const isPart =
                    r.io_manager_key?.includes("partitioned") || r.partitioned;
                  return (
                    <tr
                      key={r.id}
                      className={cn(
                        "border-b hover:bg-muted/10 transition-colors cursor-pointer",
                        isSel && "bg-muted/20"
                      )}
                      onClick={() => toggle(r.id)}
                    >
                      <td className="p-3">
                        <div className="flex justify-center">
                          <input
                            type="checkbox"
                            className="rounded"
                            checked={isSel}
                            onChange={() => toggle(r.id)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                      </td>
                      <td className="p-3 font-medium font-mono text-brand">
                        {r.name}
                        {r.tags.length > 0 && (
                          <div className="flex gap-1 mt-1 flex-wrap">
                            {r.tags.slice(0, 3).map((tag, idx) => (
                              <span 
                                key={idx} 
                                className="inline-flex text-[10px] py-0.5 px-1.5 rounded-full bg-muted/40 text-muted-foreground"
                              >
                                {tag}
                              </span>
                            ))}
                            {r.tags.length > 3 && (
                              <span className="inline-flex text-[10px] py-0.5 px-1.5 rounded-full bg-muted/40 text-muted-foreground">
                                +{r.tags.length - 3}
                              </span>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="p-3 line-clamp-2 max-w-[240px] text-muted-foreground">
                        {r.description || "-"}
                      </td>
                      <td className="p-3">
                        <span className={cn(
                          "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium",
                          isPart 
                            ? "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300"
                            : "bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-300"
                        )}>
                          {isPart ? (
                            <>
                              <Calendar className="w-3 h-3 mr-1" />
                              Partitioned
                            </>
                          ) : (
                            <>
                              <Layers className="w-3 h-3 mr-1" />
                              Single File
                            </>
                          )}
                        </span>
                      </td>
                      {/* window controls for partitioned */}
                      <td className="p-3">
                        {isPart ? (
                          <div className="flex items-center justify-center gap-2">
                            <div className="flex gap-1 items-center bg-muted/30 rounded px-2 py-0.5">
                              <input
                                type="number"
                                className="w-14 border rounded px-1 py-0.5 text-center text-xs"
                                value={r.start_year}
                                onChange={(e) =>
                                  updateWindow(
                                    r.id,
                                    "start_year",
                                    Number(e.target.value)
                                  )
                                }
                                onClick={(e) => e.stopPropagation()}
                              />
                              <span className="text-muted-foreground">-</span>
                              <input
                                type="number"
                                className="w-10 border rounded px-1 py-0.5 text-center text-xs"
                                value={r.start_month}
                                min={1}
                                max={12}
                                onChange={(e) =>
                                  updateWindow(
                                    r.id,
                                    "start_month",
                                    Number(e.target.value)
                                  )
                                }
                                onClick={(e) => e.stopPropagation()}
                              />
                            </div>
                            <span className="text-muted-foreground">to</span>
                            <div className="flex gap-1 items-center bg-muted/30 rounded px-2 py-0.5">
                              <input
                                type="number"
                                className="w-14 border rounded px-1 py-0.5 text-center text-xs"
                                value={r.end_year}
                                onChange={(e) =>
                                  updateWindow(
                                    r.id,
                                    "end_year",
                                    Number(e.target.value)
                                  )
                                }
                                onClick={(e) => e.stopPropagation()}
                              />
                              <span className="text-muted-foreground">-</span>
                              <input
                                type="number"
                                className="w-10 border rounded px-1 py-0.5 text-center text-xs"
                                value={r.end_month}
                                min={1}
                                max={12}
                                onChange={(e) =>
                                  updateWindow(
                                    r.id,
                                    "end_month",
                                    Number(e.target.value)
                                  )
                                }
                                onClick={(e) => e.stopPropagation()}
                              />
                            </div>
                          </div>
                        ) : (
                          <div className="text-center text-muted-foreground text-sm">
                            Not applicable
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        
        {/* Footer with selected count and actions */}
        {selectedCount > 0 && (
          <div className="flex justify-between items-center p-3 border rounded-lg bg-muted/20">
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1.5 text-sm">
                <Tag className="w-4 h-4 text-brand" />
                <span>{selectedCount} datasets selected</span>
              </div>
            </div>
            <Button
              onClick={handleGenerate}
              className="bg-brand hover:bg-brand/90"
            >
              <FileSymlink className="w-4 h-4 mr-2" />
              Generate Python Code
            </Button>
          </div>
        )}
      </div>

      {/* ---------------- Dialog with generated code ------------------ */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Code className="w-5 h-5 text-brand" />
              {codeMode === "python" 
                ? "custom_assets.py" 
                : codeMode === "dbt" 
                ? "sources.yml" 
                : codeMode === "datasets" 
                ? "datasets.py"
                : codeMode === "warehouse"
                ? "duckdb_warehouse.py"
                : "definitions.py"}
            </DialogTitle>
            <DialogDescription>
              Generated {codeMode === "python" || codeMode === "datasets" || codeMode === "warehouse" || codeMode === "definitions" 
                ? "Python" 
                : "dbt YAML"} code for{" "}
              {selectedIds.length} dataset
              {selectedIds.length !== 1 && "s"}
            </DialogDescription>
          </DialogHeader>

          {/* toggle between Python and dbt view */}
          <ToggleGroup
            type="single"
            value={codeMode}
            onValueChange={(v) => {
              if (v) {
                setCodeMode(v as typeof codeMode);
                setCopied(false);
              }
            }}
            className="mb-3"
          >
            <ToggleGroupItem value="python">custom_assets.py</ToggleGroupItem>
            <ToggleGroupItem value="dbt">sources.yml</ToggleGroupItem>
            <ToggleGroupItem value="datasets">datasets.py</ToggleGroupItem>
            <ToggleGroupItem value="warehouse">duckdb_warehouse.py</ToggleGroupItem>
            <ToggleGroupItem value="definitions">definitions.py</ToggleGroupItem>
          </ToggleGroup>

          <div className="relative">
            <div className="bg-slate-950 rounded-lg shadow-inner">
              <pre className="text-slate-50 p-5 text-sm font-mono max-h-[50vh] overflow-auto">
              {codeMode === "python"
                ? pythonCode
                : codeMode === "dbt"
                ? dbtCode
                : codeMode === "datasets"
                ? datasetsCode
                : codeMode === "warehouse"
                ? warehouseCode
                : DEFINITIONS_TEMPLATE}
              </pre>
              <div className="border-t border-slate-800 p-3 bg-slate-900 text-slate-300 text-xs rounded-b-lg">
                <div className="flex items-start gap-2">
                  <Settings className="w-4 h-4 text-brand mt-0.5 flex-shrink-0" />
                  <span>
                    {codeMode === "python" 
                      ? "Copy and paste these contents into the file pipeline/inegestion/custom_assets. This tells Dagster the assets you are ingesting. You can change the name of the file by changing the import statement import pipeline.assets.ingestion.custom_assets as ingestion_module to your new file name in your definitions.py. When you are ready to add DBT and SQL transformations to your pipeline, you can add the rest of the produced files to your code."
                      : codeMode === "dbt"
                      ? "Copy and paste these contents into the file transformations/dbt/models/sources.yml. This tells DBT what your source raw tables are for your SQL transformation pipeline. Any .sql files you add into the folder transformations/dbt/models will be added to DBT under the group DBT Transformations."
                      : codeMode === "datasets"
                      ? "Copy and paste these contents into the file pipeline/datasets.py, This creates a mapping for DuckDB so it knows how to create views for both datasets that are a single file, and datasets that are made up of multiple hive partitioned files"
                      : codeMode === "warehouse"
                      ? "Copy and paste these contents into the file pipeline/assets/warehouse/duckdb_warehouse.py. This contains logic to create our views in DuckDB, and ensure Dagster does not attempt to do this until all raw datasets finish downloading"
                      : "Copy and paste these contents into the file pipeline/definitions.py. This adds DBT and our DBT assets into our Dagster project. After adding all the files, simply run ./setup.sh to reload the Dagster UI and see your new end to end pipeline"
                    }
                  </span>
                </div>
              </div>
            </div>
            <div className="absolute top-3 right-3">
              <Button
                size="sm"
                variant="outline"
                className="bg-slate-800 text-slate-200 hover:bg-slate-700 border-slate-700"
                onClick={handleCopy}
              >
                {copied ? (
                  <>
                    <Check className="w-3.5 h-3.5 mr-1" /> Copied!
                  </>
                ) : (
                  <>
                    <ClipboardCopy className="w-3.5 h-3.5 mr-1" /> Copy
                  </>
                )}
              </Button>
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Close
            </Button>
            <Button
              variant="outline"
              onClick={() =>
                download(
                  codeMode === "python"
                    ? "custom_assets.py"
                    : codeMode === "dbt"
                    ? "sources.yml"
                    : codeMode === "datasets"
                    ? "datasets.py"
                    : codeMode === "warehouse"
                    ? "duckdb_warehouse.py"
                    : "definitions.py",
                  codeMode === "python"
                    ? pythonCode
                    : codeMode === "dbt"
                    ? dbtCode
                    : codeMode === "datasets"
                    ? datasetsCode
                    : codeMode === "warehouse"
                    ? warehouseCode
                    : DEFINITIONS_TEMPLATE,
                )
              }
            >
              <FileSymlink className="w-4 h-4 mr-2" />
              Download
            </Button>
            <Button
              className="bg-brand hover:bg-brand/90"
              onClick={handleCopy}
            >
              {copied ? "Copied!" : "Copy to Clipboard"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      
      {/* Help dialog */}
      <Dialog open={helpOpen} onOpenChange={setHelpOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <HelpCircle className="w-5 h-5 text-brand" />
              Asset Generator Help
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-2">
            <p>
              The Asset Generator creates Python code for Dagster assets from your selected datasets.
            </p>
            
            <Card className="p-4 border-amber-200 bg-amber-50 dark:bg-amber-950/20">
              <h3 className="font-medium mb-2 flex items-center gap-2">
                <Calendar className="w-4 h-4 text-amber-600" />
                <span className="text-amber-900 dark:text-amber-400">Partitioned Datasets</span>
              </h3>
              <p className="text-sm text-amber-800 dark:text-amber-300">
                For partitioned datasets, you can configure the date window by adjusting the start and end values. 
                The format is YYYY-MM (year and month).
              </p>
            </Card>
            
            <div className="space-y-2">
              <h3 className="font-medium">Usage Instructions:</h3>
              <ol className="list-decimal list-inside space-y-1 text-sm pl-2">
                <li>Select datasets using checkboxes or bulk selection tools</li>
                <li>Configure partition windows for date-partitioned datasets</li>
                <li>Click "Generate Assets" to create the Python code</li>
                <li>Copy the generated code to your clipboard</li>
                <li>Paste into your Dagster project's <code className="bg-muted px-1 py-0.5 rounded text-xs">custom_assets.py</code> file</li>
              </ol>
            </div>
          </div>
          
          <div className="flex justify-end mt-4">
            <Button onClick={() => setHelpOpen(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
